import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePublicProject } from '@/hooks/api/use-public';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  MapPin, 
  Calendar, 
  Building2, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Award,
  Shield
} from 'lucide-react';
import { useState } from 'react';
import { getFullImageUrl } from '@/lib/image-utils';

const ProjectDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const { data: response, isLoading, error } = usePublicProject(Number(id));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const project = response?.data;
  const isRTL = language === 'ar';

  const getTitle = () => {
    if (!project) return '';
    const title = project.title as { en?: string; ar?: string };
    return isRTL ? (title.ar || title.en) : (title.en || title.ar);
  };

  const getDescription = () => {
    if (!project) return '';
    const desc = project.description as { en?: string; ar?: string } | undefined;
    if (!desc) return '';
    return isRTL ? (desc.ar || desc.en) : (desc.en || desc.ar);
  };

  const getLocation = () => {
    if (!project || !project.location) return '';
    const loc = project.location as { en?: string; ar?: string };
    return isRTL ? (loc.ar || loc.en) : (loc.en || loc.ar);
  };

  const images = project?.images || [];
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="container mx-auto px-4 py-12">
          <Skeleton className="h-96 w-full rounded-2xl mb-8" />
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <Building2 className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            {t('portfolio.projectNotFound') || 'المشروع غير موجود'}
          </h2>
          <p className="text-slate-600 mb-6">
            {t('portfolio.projectNotFoundDesc') || 'لم نتمكن من العثور على هذا المشروع'}
          </p>
          <Link to="/portfolio">
            <Button>
              <ArrowRight className="h-4 w-4 mr-2 rtl:rotate-180" />
              {t('portfolio.backToProjects') || 'العودة إلى المشاريع'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10">
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 py-6">
          <nav className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-primary transition-colors">
              {t('common.home') || 'الرئيسية'}
            </Link>
            <span className="rtl:rotate-180">/</span>
            <Link to="/portfolio" className="hover:text-primary transition-colors">
              {t('portfolio.title') || 'معرض الأعمال'}
            </Link>
            <span className="rtl:rotate-180">/</span>
            <span className="text-slate-900 font-medium">{getTitle()}</span>
          </nav>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images Section */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-xl">
                {images.length > 0 ? (
                  <>
                    <img
                      src={getFullImageUrl(images[currentImageIndex]?.image_url) || ''}
                      alt={getTitle()}
                      className="w-full h-full object-cover"
                    />
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronLeft className="h-6 w-6 text-slate-900" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronRight className="h-6 w-6 text-slate-900" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                    <div className="w-24 h-24 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                      <Building2 className="w-12 h-12 text-primary" />
                    </div>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {hasMultipleImages && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentImageIndex
                          ? 'border-primary shadow-lg'
                          : 'border-transparent hover:border-slate-300'
                      }`}
                    >
                      <img
                        src={getFullImageUrl(img.image_url) || ''}
                        alt={`${getTitle()} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="space-y-8">
              {/* Title & Meta */}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {getTitle()}
                </h1>
                
                <div className="flex flex-wrap gap-4 text-slate-600">
                  {project.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      <span>{getLocation()}</span>
                    </div>
                  )}
                  {project.completed_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span>{project.completed_date}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {project.description && (
                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    {t('portfolio.description') || 'الوصف'}
                  </h3>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {getDescription()}
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-100 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">
                    {t('portfolio.highQuality') || 'جودة عالية'}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-100 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">
                    {t('portfolio.professionalExecution') || 'تنفيذ احترافي'}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-100 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">
                    {t('portfolio.comprehensiveGuarantees') || 'ضمانات متكاملة'}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="gap-2">
                    {t('portfolio.requestSimilar') || 'اطلب مشروع مشابه'}
                    <Sparkles className="h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" size="lg" className="gap-2">
                    <ArrowRight className="h-5 w-5 rtl:rotate-180" />
                    {t('portfolio.backToProjects') || 'العودة إلى المشاريع'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
